import { useEffect, useState } from 'react';
import './App.css';

const benefitItems = [
  {
    title: 'Your life stays in sequence',
    copy: 'Bring scattered camera-roll moments into one private photo timeline that is easy to browse by memory, mood, and season.',
  },
  {
    title: 'Memories feel easier to revisit',
    copy: 'Recatch is designed to help you return to everyday photos without hunting through folders, duplicate screenshots, or old chats.',
  },
  {
    title: 'The experience stays personal',
    copy: 'The product is built for reflection first, with a calmer interface that keeps the focus on your people, places, and moments.',
  },
];

const steps = [
  {
    title: 'Save everyday highlights',
    copy: 'Keep the photos that tell the real story of your week, not just the ones you post.',
  },
  {
    title: 'See them in context',
    copy: 'Browse moments as a living timeline so memories feel connected instead of buried.',
  },
  {
    title: 'Return to them anytime',
    copy: 'Recatch makes it easier to relive the people, places, and details you want to hold on to.',
  },
];

const faqs = [
  {
    question: 'What is Recatch?',
    answer:
      'Recatch is a private photo timeline app focused on helping you organize and revisit everyday memories with less noise and less effort.',
  },
  {
    question: 'Who is the beta for?',
    answer:
      'The closed beta is for early users who want a simpler, more personal way to keep up with photos and shape the product before launch.',
  },
  {
    question: 'What happens after I join the waitlist?',
    answer:
      'You will be added to the waitlist and contacted when new beta spots open. Early signups help prioritize feedback and launch access.',
  },
];

function ensureMeta(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function ensureLink(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement('link');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function ensureStructuredData(id, data) {
  let element = document.head.querySelector(`#${id}`);

  if (!element) {
    element = document.createElement('script');
    element.type = 'application/ld+json';
    element.id = id;
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(data);
}

function PhonePreview() {
  return (
    <figure className="phone-preview" aria-label="Recatch app preview">
      <img src="/phone.svg" alt="Recatch app shown on a smartphone" className="phone-preview__img" />
    </figure>
  );
}

function LandingPage() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitState, setSubmitState] = useState('idle');

  useEffect(() => {
    const siteUrl = (process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/$/, '');
    const title = 'Recatch | Private Photo Timeline App for Everyday Memories';
    const description =
      'Recatch is a private photo timeline app that helps you organize, revisit, and hold on to the everyday memories that matter most.';
    const canonicalUrl = `${siteUrl}/`;
    const imageUrl = `${siteUrl}/landing_page_example.png`;

    document.title = title;

    ensureMeta('meta[name="description"]', { name: 'description', content: description });
    ensureMeta('meta[name="robots"]', { name: 'robots', content: 'index,follow,max-image-preview:large' });
    ensureMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    ensureMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    ensureMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    ensureMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
    ensureMeta('meta[property="og:image"]', { property: 'og:image', content: imageUrl });
    ensureMeta('meta[property="og:image:alt"]', {
      property: 'og:image:alt',
      content: 'Preview of the Recatch landing page and product imagery.',
    });
    ensureMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    ensureMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    ensureMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    ensureMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: imageUrl });
    ensureLink('link[rel="canonical"]', { rel: 'canonical', href: canonicalUrl });

    ensureStructuredData('recatch-website-schema', {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Recatch',
      url: canonicalUrl,
      description,
      image: imageUrl,
      publisher: {
        '@type': 'Organization',
        name: 'Recatch',
        sameAs: ['https://x.com/heyMrZak'],
      },
    });
  }, []);

  const parseApiResponse = async (response) => {
    const contentType = response.headers.get('content-type') || '';
    const raw = await response.text();

    if (!raw) {
      return {};
    }

    const looksLikeJson = contentType.includes('application/json');
    if (looksLikeJson) {
      try {
        return JSON.parse(raw);
      } catch {
        return { message: 'Server returned invalid JSON.' };
      }
    }

    if (raw.trim().startsWith('<')) {
      return {
        message: 'API is unreachable or returned HTML. Start both client and server with npm start.',
      };
    }

    return { message: raw };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');
    setSubmitState('idle');

    try {
      const response = await fetch('/api/waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, website: '' }),
      });

      const payload = await parseApiResponse(response);
      if (!response.ok) {
        throw new Error(payload?.message || 'Could not join the waitlist right now.');
      }

      setSubmitState('success');
      setSubmitMessage(payload?.message || 'You are on the waitlist.');
      setEmail('');
    } catch (error) {
      setSubmitState('error');
      setSubmitMessage(error instanceof Error ? error.message : 'Something went wrong.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-shell">
      <header className="landing-header">
        <a href="#top" className="brand-wordmark">
          recatch.
        </a>
        <p className="brand-pill">Closed beta</p>
      </header>

      <main id="top" className="landing-main">
        <div className="content-stack">
          <section id="waitlist" className="hero-grid" aria-labelledby="hero-title">
            <article className="hero-copy">
              <p className="hero-kicker">Capture the moments that matter</p>
              <h1 id="hero-title" className="hero-title">
                <span>Now. Then.</span>
                <span>Always.</span>
              </h1>
              <p className="hero-subtitle">
                Recatch is a private photo timeline app that helps you organize everyday memories, revisit them with context, and keep the moments that matter close.
              </p>

              <form onSubmit={handleSubmit} className="waitlist-form" aria-label="Waitlist form">
                <label htmlFor="email" className="sr-only">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  disabled={isSubmitting}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="example@mail.com"
                  className="waitlist-input"
                />
                <button type="submit" className="waitlist-button" disabled={isSubmitting}>
                  {isSubmitting ? 'Joining...' : 'Join the waitlist'}
                </button>
              </form>

              {submitMessage ? (
                <p className={`waitlist-feedback waitlist-feedback--${submitState}`} role="status" aria-live="polite">
                  {submitMessage}
                </p>
              ) : null}

              <div className="social-proof" aria-label="Social proof">
                <div className="social-proof__avatars">
                  <img src="/avatar1.jpg" alt="Community member" className="avatar-chip" />
                  <img src="/avatar2.jpg" alt="Community member" className="avatar-chip avatar-chip--stack" />
                  <img src="/avatar3.jpg" alt="Community member" className="avatar-chip avatar-chip--stack" />
                </div>
                <p className="social-proof__text">Join +700 others</p>
              </div>
            </article>

            <article className="phone-stage" aria-label="App preview">
              <div className="phone-stage__glow" aria-hidden="true" />
              <PhonePreview />
              <img src="/girl.jpg" alt="Friend memory" className="float-memory float-memory--a" />
              <img src="/lake.jpg" alt="Lake memory" className="float-memory float-memory--b" />
              <img src="/street.jpg" alt="Street memory" className="float-memory float-memory--c" />
              <img src="/man.jpg" alt="Portrait memory" className="float-memory float-memory--d" />
            </article>
          </section>

          <section className="content-section" aria-labelledby="benefits-title">
            <p className="section-eyebrow">Why Recatch</p>
            <h2 id="benefits-title" className="section-title">
              A calmer way to keep up with your photos
            </h2>
            <p className="section-intro">
              Most photo apps are built for storage or sharing. Recatch is built for memory, helping you return to everyday moments without losing them in a crowded camera roll.
            </p>

            <div className="benefits-grid">
              {benefitItems.map((item) => (
                <article key={item.title} className="benefit-card">
                  <h3 className="benefit-title">{item.title}</h3>
                  <p className="benefit-copy">{item.copy}</p>
                </article>
              ))}
            </div>
          </section>

          <section id="how-it-works" className="content-section" aria-labelledby="steps-title">
            <p className="section-eyebrow">How it works</p>
            <h2 id="steps-title" className="section-title">
              Keep the story behind the photo
            </h2>
            <p className="section-intro">
              The product is designed around a simple idea: everyday memories become more meaningful when they are easy to save, revisit, and understand in sequence.
            </p>

            <div className="steps-grid">
              {steps.map((step, index) => (
                <article key={step.title} className="step-card">
                  <p className="step-index">0{index + 1}</p>
                  <h3 className="benefit-title">{step.title}</h3>
                  <p className="benefit-copy">{step.copy}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="content-section" aria-labelledby="faq-title">
            <p className="section-eyebrow">FAQ</p>
            <h2 id="faq-title" className="section-title">
              Questions about the waitlist
            </h2>

            <div className="faq-list">
              {faqs.map((item) => (
                <details key={item.question} className="faq-item">
                  <summary className="faq-question">{item.question}</summary>
                  <p className="faq-answer">{item.answer}</p>
                </details>
              ))}
            </div>
          </section>

          <section className="cta-panel" aria-labelledby="cta-title">
            <p className="section-eyebrow">Early access</p>
            <h2 id="cta-title" className="cta-title">
              Join the closed beta for a more personal photo timeline
            </h2>
            <p className="cta-copy">
              Sign up for early access and get updates as Recatch opens more spots for people who want a better way to revisit the moments they care about.
            </p>
            <a href="#waitlist" className="cta-link">
              Go to waitlist form
            </a>
          </section>
        </div>
      </main>

      <footer className="page-signature" aria-label="Signature">
        <p className="signature-line">
          made with memory. by{' '}
          <a href="https://x.com/heyMrZak" target="_blank" rel="noreferrer" className="signature-link">
            MrZak
          </a>
          <span className="signature-separator" aria-hidden="true">
            |
          </span>
          <a href="/privacy.html" className="signature-link signature-link--privacy">
            Privacy Notice
          </a>
        </p>
      </footer>
    </div>
  );
}

export default LandingPage;
